import React, { useEffect, useCallback, type ReactNode } from 'react';
import {
  Modal as RNModal,
  Pressable,
  type ViewStyle,
  KeyboardAvoidingView,
  Platform,
  Animated,
} from 'react-native';
// Class-aware scheme hook (reads `<html data-theme>` on web) — NOT RN's
// `useColorScheme`, which reads the OS `prefers-color-scheme` and renders the
// wrong palette under the app's manual dark theme (repo styling rule 3).
import { useColorScheme } from '../../foundations/colorScheme';
import { tokens } from '../../design-system/tokens';

export interface ModalProps {
  visible: boolean;
  onClose: () => void;
  closeOnOverlayPress?: boolean;
  children: ReactNode;
  accessibilityLabel?: string;
  /** Override the default 480px cap on the dialog width (e.g. a wider sheet).
   *  Any RN width value — a number (px) or a percentage string. */
  maxWidth?: ViewStyle['maxWidth'];
}

export const Modal: React.FC<ModalProps> = ({
  visible,
  onClose,
  closeOnOverlayPress = true,
  children,
  accessibilityLabel,
  maxWidth,
}) => {
  const scheme = useColorScheme();
  const colorScheme = scheme === 'dark' ? 'dark' : 'light';
  const colors = tokens.color.semantic[colorScheme];

  const fadeAnim = React.useRef(new Animated.Value(0)).current;
  const scaleAnim = React.useRef(new Animated.Value(1)).current;

  useEffect(() => {
    if (visible) {
      // Open is INSTANT — no grow-in. Both fade and scale snap to their
      // target over the `instant` (0ms) duration, so the modal appears
      // the same frame it mounts rather than visibly settling in. (The
      // previous scale spring took ~250ms to settle, which read as lag.)
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: tokens.motion.duration.instant,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(scaleAnim, {
          toValue: 1,
          duration: tokens.motion.duration.instant,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: tokens.motion.duration.fast,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(scaleAnim, {
          toValue: 0.9,
          duration: tokens.motion.duration.fast,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ]).start();
    }
  }, [visible, fadeAnim, scaleAnim]);

  const handleOverlayPress = useCallback(() => {
    if (closeOnOverlayPress) {
      onClose();
    }
  }, [closeOnOverlayPress, onClose]);

  const handleContentPress = useCallback((event: any) => {
    event.stopPropagation();
  }, []);

  return (
    <RNModal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
      statusBarTranslucent
      accessibilityViewIsModal
      accessibilityLabel={accessibilityLabel ?? 'Modal dialog'}
    >
      <Animated.View
        style={[
          styles.overlay,
          { backgroundColor: colors.overlay.scrim },
          { opacity: fadeAnim },
        ]}
      >
        <Pressable style={styles.overlayPressable} onPress={handleOverlayPress}>
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            style={styles.keyboardAvoidingView}
          >
            <Pressable onPress={handleContentPress}>
              <Animated.View
                style={[
                  styles.content,
                  { backgroundColor: colors.surface.panel },
                  ...(maxWidth !== undefined ? [{ maxWidth }] : []),
                  {
                    transform: [{ scale: scaleAnim }],
                  },
                ]}
              >
                {children}
              </Animated.View>
            </Pressable>
          </KeyboardAvoidingView>
        </Pressable>
      </Animated.View>
    </RNModal>
  );
};

const styles = {
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  } satisfies ViewStyle,

  overlayPressable: {
    flex: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  } satisfies ViewStyle,

  keyboardAvoidingView: {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  } satisfies ViewStyle,

  content: {
    borderRadius: tokens.radius.lg,
    padding: tokens.spacing.xl,
    maxWidth: 480,
    width: '100%',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 8,
  } satisfies ViewStyle,
};
